// A TABLE field's stored content, read the same way by the report view and the template editor.
import { z } from "zod";

import { JSONDatumInterface } from "@fronterahealth/frontera-ui-components/TableBuilderDynamic/TableBuilderDynamic";

import { parseJson } from "@components/FieldComponent/utils";

// Rows may be stored either as header-keyed records or as positional string
// arrays (e.g. `[["", "", ""]]`). TableBuilderDynamic only understands records,
// so array rows are accepted here and normalized to records below.
const tableRowSchema = z.union([z.record(z.string(), z.unknown()), z.array(z.string())]);

const tableContentSchema = z.object({
  headers: z.array(z.string()),
  data: z.array(tableRowSchema),
  customRowAttributeField: z
    .string()
    .nullish()
    .transform((value) => value ?? undefined),
});

export const SAMPLE_TABLE_CONTENT: JSONDatumInterface = {
  headers: ["Enter Header 1", "Enter Header 2"],
  data: [],
};

export type TableContentIssues = z.ZodError["issues"];

type TableContentResult = { success: true; data: JSONDatumInterface } | { success: false; issues: TableContentIssues };

const normalizeRow = (row: Record<string, unknown> | string[], headers: string[]): Record<string, unknown> =>
  Array.isArray(row) ? Object.fromEntries(headers.map((header, index) => [header, row[index] ?? ""])) : row;

export const parseTableContent = (
  value: string | null | undefined,
  emptyContent: JSONDatumInterface,
): TableContentResult => {
  const result = tableContentSchema.safeParse(value?.trim() ? parseJson<unknown>(value) : emptyContent);
  if (!result.success) return { success: false, issues: result.error.issues };
  const { headers, data } = result.data;
  return { success: true, data: { ...result.data, data: data.map((row) => normalizeRow(row, headers)) } };
};
